import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Minus, Sparkles } from 'lucide-react';
import { aiApi } from '../lib/api';

/**
 * Floating HR assistant — light pink / white theme.
 *
 * The browser only ever sends the typed question: the AI key stays on the
 * server and /api/ai/chat applies the signed-in user's own permissions before
 * any data reaches the model.
 */
const COMPANY = 'Advanced Anmol Metcomp Pvt Ltd';

const SUGGESTIONS = [
  'How many employees are working?',
  'Who joined this month?',
  'Headcount by department',
  'Birthdays this month',
];

// Assistant identity: sky -> blue, the "AI" accent from the app-wide
// colour map, so the panel reads as part of the product.
const C = {
  ink: '#172554',        // headings
  deep: '#334155',       // body text
  primary: '#2563EB',    // actions, user bubble (blue)
  primaryDk: '#7C3AED',  // gradient end (violet)
  accent: '#38BDF8',     // sky spark
  sky: '#06B6D4',        // gradient start (cyan)
  line: 'rgba(37,99,235,0.16)',
  tint: 'rgba(56,189,248,0.10)',
  tintDeep: '#E0F2FE',
  muted: '#64748B',      // secondary text
};
const GRAD = `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDk} 100%)`;
const HEADER_GRAD =
  `linear-gradient(120deg, #3B82F6 0%, #4F46E5 55%, #7C3AED 100%)`;

/**
 * The assistant's mascot — a friendly robot with its arms up, greeting you.
 *
 * Drawn as inline SVG rather than shipped as an image: it stays crisp at any
 * size, adds no network request, and carries no colour profile of its own, so
 * it sits on the panel's tinted background without a visible box. Decorative,
 * so it is hidden from screen readers — the heading beside it already says
 * what this is.
 */
/**
 * The assistant's robot, drawn rather than loaded so it stays crisp at every
 * size and ships no image request. White casing, a dark visor with two lit
 * eyes, and a hologram ring it hovers over — `ring={false}` drops the ring for
 * the small launcher badge, where there is no room for it.
 */
function Mascot({ size = 96, className = '', ring = true, id = 'm', track = false }) {
  const u = (n) => `${id}-${n}`;
  const svgRef = useRef(null);
  const headRef = useRef(null);
  const eyeRef = useRef(null);

  // Head and eyes lean toward the pointer. Written straight to the DOM inside
  // one rAF rather than through state: a mousemove handler that re-renders
  // React on every pixel is the usual way this ends up costing more than it
  // is worth. The listener is passive and the frame is coalesced, so a fast
  // sweep across the screen still costs one write per frame.
  useEffect(() => {
    if (!track) return undefined;
    const reduce = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    let frame = 0;
    let px = 0;
    let py = 0;

    const apply = () => {
      frame = 0;
      const el = svgRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      // -1 .. 1 either side of the robot, then clamped so it never looks
      // dislocated — the head only ever leans a couple of pixels.
      const nx = Math.max(-1, Math.min(1, (px - (r.left + r.width / 2)) / (r.width * 1.6)));
      const ny = Math.max(-1, Math.min(1, (py - (r.top + r.height / 2)) / (r.height * 1.6)));
      if (headRef.current) {
        headRef.current.style.transform = `translate(${nx * 2.4}px, ${ny * 1.6}px)`;
      }
      if (eyeRef.current) {
        eyeRef.current.style.transform = `translate(${nx * 3.2}px, ${ny * 2.2}px)`;
      }
    };

    const onMove = (e) => {
      px = e.clientX;
      py = e.clientY;
      if (!frame) frame = requestAnimationFrame(apply);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [track]);

  return (
    <svg ref={svgRef} width={size} height={size} viewBox="0 0 120 124" fill="none"
         className={className} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={u('shell')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="58%" stopColor="#E3F1FF" />
          <stop offset="100%" stopColor="#AFD3F2" />
        </linearGradient>
        <linearGradient id={u('visor')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2B3550" />
          <stop offset="100%" stopColor="#141B2E" />
        </linearGradient>
        <linearGradient id={u('trim')} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7DD3FC" />
          <stop offset="100%" stopColor="#38BDF8" />
        </linearGradient>
        <radialGradient id={u('ring')} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#7DD3FC" stopOpacity="0.95" />
          <stop offset="70%" stopColor="#38BDF8" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0" />
        </radialGradient>
        <filter id={u('glow')} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {ring && (
        <g className="ai-ring" style={{ transformOrigin: '60px 108px' }}>
          {/* Soft halo: the casing is near-white, so on a pale panel it needs
              something behind it to separate from the background. */}
          <ellipse cx="60" cy="60" rx="52" ry="54" fill={`url(#${u('ring')})`} opacity="0.22" />
          <ellipse cx="60" cy="108" rx="40" ry="10" fill={`url(#${u('ring')})`} />
          <ellipse cx="60" cy="108" rx="27" ry="6.2" fill="none"
                   stroke="#7DD3FC" strokeWidth="1.6" opacity="0.75" />
          <ellipse cx="60" cy="108" rx="15" ry="3.4" fill="none"
                   stroke="#BAE6FD" strokeWidth="1.2" opacity="0.6" />
        </g>
      )}

      <g className="ai-bounce" style={{ transformOrigin: '60px 104px' }}>
        {/* Arms, behind the body so they read as coming from its sides */}
        <g fill={`url(#${u('shell')})`} stroke="#7FB4E3" strokeWidth="1.4">
          <rect x="12" y="58" width="13" height="26" rx="6.5"
                transform="rotate(-18 18.5 71)" />
          <rect x="95" y="58" width="13" height="26" rx="6.5"
                transform="rotate(18 101.5 71)" />
        </g>

        {/* Body */}
        <path d="M60 66c-14 0-22 7-22 18v8c0 7 9 11 22 11s22-4 22-11v-8c0-11-8-18-22-18z"
              fill={`url(#${u('shell')})`} stroke="#7FB4E3" strokeWidth="1.6" />
        <rect x="52" y="78" width="16" height="4.5" rx="2.25" fill="#BAE6FD" />

        {/* Neck */}
        <rect x="53" y="56" width="14" height="10" rx="4" fill="#BBD9F4" />

        {/* Side pods */}
        <ellipse cx="25" cy="38" rx="8" ry="10.5" fill={`url(#${u('shell')})`}
                 stroke="#7FB4E3" strokeWidth="1.4" />
        <ellipse cx="95" cy="38" rx="8" ry="10.5" fill={`url(#${u('shell')})`}
                 stroke="#7FB4E3" strokeWidth="1.4" />

        {/* Head. `headRef` carries the cursor lean, so the whole head turns a
            little toward the pointer while the body stays put. */}
        <g ref={headRef} style={{ transition: 'transform 140ms ease-out' }}>
          <rect x="26" y="14" width="68" height="48" rx="20"
                fill={`url(#${u('shell')})`} stroke="#7FB4E3" strokeWidth="1.6" />
          {/* Visor trim, then the visor itself */}
          <rect x="33" y="20" width="54" height="34" rx="15" fill={`url(#${u('trim')})`} />
          <rect x="35.5" y="22.5" width="49" height="29" rx="13" fill={`url(#${u('visor')})`} />
          {/* Eyes. Two nested groups on purpose: the OUTER one takes the
              cursor offset as an inline transform, the INNER one runs the
              blink. A CSS animation wins over an inline style on the same
              element, so sharing a node would silently cancel the tracking. */}
          <g ref={eyeRef} style={{ transition: 'transform 90ms linear' }}>
            <g className="ai-eyes" filter={`url(#${u('glow')})`}
               style={{ transformOrigin: '60px 37px' }}>
              <ellipse cx="50" cy="37" rx="6.2" ry="6.6" fill="#8AE2FF" />
              <ellipse cx="70" cy="37" rx="6.2" ry="6.6" fill="#8AE2FF" />
              <ellipse cx="50" cy="35.4" rx="2.4" ry="2.6" fill="#FFFFFF" />
              <ellipse cx="70" cy="35.4" rx="2.4" ry="2.6" fill="#FFFFFF" />
            </g>
          </g>
          {/* Antenna */}
          <rect x="58.5" y="6" width="3" height="9" rx="1.5" fill="#A9CDEC" />
          <circle cx="60" cy="5" r="3.4" fill="#7DD3FC" filter={`url(#${u('glow')})`} />
        </g>
      </g>
    </svg>
  );
}

/**
 * Render the model's light markdown as React nodes.
 *
 * Built as elements rather than HTML so model output can never inject markup —
 * there is no dangerouslySetInnerHTML anywhere in this component. Handles the
 * three things the model actually emits: **bold**, "## heading" and bullets.
 */
function formatAnswer(text) {
  return String(text ?? '').split('\n').map((raw, li) => {
    const heading = /^\s*#{1,6}\s+/.test(raw);
    let line = raw.replace(/^\s*#{1,6}\s+/, '').replace(/^\s*[-*]\s+/, '\u2022 ');
    if (!line.trim()) return <div key={li} style={{ height: 6 }} />;
    const parts = line.split(/\*\*(.+?)\*\*/g).map((seg, si) => (
      si % 2 ? <strong key={si}>{seg}</strong> : <span key={si}>{seg}</span>
    ));
    return (
      <div key={li} style={heading ? { fontWeight: 600, margin: '4px 0 2px' } : undefined}>
        {parts}
      </div>
    );
  });
}

export default function AiAssistant() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    aiApi.status()
      .then((r) => setEnabled(!!r.data?.enabled))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs, busy]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const ask = useCallback(async (question) => {
    const q = (question ?? '').trim();
    if (!q || busy) return;
    // Only the plain turns go back as context; errors stay local to the UI.
    // Errors stay local to the UI, and a verbatim report is evidence pasted
    // into the panel — feeding either back as context would invite the model
    // to restate an audit it never produced.
    const history = msgs.filter((m) => !m.error && !m.verbatim)
      .map((m) => ({ role: m.role, content: m.content }));
    setMsgs((s) => [...s, { role: 'user', content: q }]);
    setText('');
    setBusy(true);
    try {
      const r = await aiApi.chat(q, history);
      setMsgs((s) => [...s, { role: 'assistant', content: r.data.answer }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsgs((s) => [...s, {
        role: 'assistant', error: true,
        content: typeof detail === 'string' ? detail
          : 'Sorry — I could not reach the assistant. Please try again.',
      }]);
    } finally { setBusy(false); }
  }, [busy, msgs]);

  // Reports other screens want shown in this panel. The text is printed
  // verbatim and is NEVER sent to the model — it is already the answer, and a
  // provenance report must not be paraphrased by anything.
  useEffect(() => {
    const onPush = (e) => {
      const { text, title } = e.detail || {};
      if (!text) return;
      setMsgs((s) => [...s, { role: 'assistant', verbatim: true, title, content: text }]);
      setOpen(true);
    };
    window.addEventListener('ai-assistant:push', onPush);
    return () => window.removeEventListener('ai-assistant:push', onPush);
  }, []);

  if (!enabled) return null;

  /** The AI mark: logo in a white disc, wrapped in a slowly rotating
   *  conic ring. Both layers are transforms, so the halo costs nothing. */
  const orb = (size, halo) => (
    <span className="relative flex-shrink-0 inline-flex" style={{ width: size, height: size }}>
      <span className="relative rounded-full bg-white flex items-center justify-center overflow-hidden
                       transition-transform duration-200 group-hover:rotate-[4deg]"
            style={{ width: size, height: size, boxShadow: halo ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none' }}>
        <img src="/assets/aampl-logo.jpg" alt="" style={{ width: size * 0.72, height: size * 0.72 }}
             className="object-contain"
             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      </span>
    </span>
  );

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="ai-assistant-open"
        aria-label="Open AI Assistant"
        title="AI Assistant"
        className="group fixed bottom-4 right-5 z-[60] inline-flex items-end justify-center
                   rounded-full transition-transform duration-200 ease-smooth
                   hover:scale-[1.07] focus-visible:outline-none
                   focus-visible:ring-2 focus-visible:ring-[#38BDF8] focus-visible:ring-offset-2"
        style={{ width: 84, height: 88, background: 'transparent', border: 0, padding: 0 }}
      >
        {/* Just the robot. No pill, no label — it hovers on its own hologram
            ring and the ring doubles as its shadow, so nothing is needed
            behind it. The whole square is the hit area, which is larger than
            the artwork so it stays easy to press on a phone. */}
        <Mascot size={84} id="ai-launch" track />
      </button>
    );
  }

  return (
    <div
      data-testid="ai-assistant-panel"
      role="dialog"
      aria-label="AI Assistant"
      className="fixed z-[60] flex flex-col overflow-hidden
                 inset-x-0 bottom-0 top-0 rounded-none
                 sm:inset-auto sm:bottom-5 sm:right-5 sm:top-auto
                 sm:w-[400px] sm:h-[74vh] sm:max-h-[640px] sm:rounded-3xl
                 "
      style={{ border: '1px solid rgba(255,255,255,0.8)',
               background: 'rgba(255,255,255,0.84)',
               backdropFilter: 'blur(22px) saturate(170%)',
               WebkitBackdropFilter: 'blur(22px) saturate(170%)',
               boxShadow: '0 20px 48px -18px rgba(23,37,84,0.32)' }}
    >
      {/* Header — the one saturated surface; everything below stays light */}
      <div className="relative flex items-center justify-between px-4 py-3.5 flex-shrink-0 overflow-hidden"
           style={{ background: HEADER_GRAD }}>
        <div className="relative flex items-center gap-2.5 min-w-0">
          {orb(38, true)}
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold leading-tight flex items-center gap-1.5 text-white">
              AI Assistant
              <Sparkles className="w-3 h-3" style={{ color: C.accent }} strokeWidth={2.2} />
            </div>
            <div className="text-[9.5px] tracking-[0.05em] leading-tight truncate"
                 style={{ color: '#C7D2FE' }}>
              {COMPANY.toUpperCase()}
            </div>
          </div>
        </div>
        <div className="relative flex items-center gap-0.5">
          <button onClick={() => setOpen(false)} aria-label="Minimize"
            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/20
                       transition-all hover:scale-110"><Minus className="w-4 h-4" /></button>
          <button onClick={() => { setOpen(false); setMsgs([]); }} aria-label="Close"
            data-testid="ai-assistant-close"
            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/20
                       transition-all hover:scale-110"><X className="w-4 h-4" /></button>
        </div>
      </div>

      <div ref={scroller} className="flex-1 overflow-y-auto px-3.5 py-4 space-y-2.5"
           style={{ background: 'linear-gradient(180deg, rgba(56,189,248,0.09), rgba(2,132,199,0.05))' }}>
        {msgs.length === 0 && (
          <div>
            <div className="text-center pb-4">
              <div data-testid="ai-mascot" className="mx-auto mb-2 flex items-end justify-center"
                   style={{ height: 104 }}>
                <Mascot size={104} id="ai-hero" track />
              </div>
              <div className="text-[18px] font-bold font-display tracking-[-0.02em] grad-text">
                Welcome to AI Assistant
              </div>
              <div className="mx-auto mt-2 rounded-full" style={{ width: 44, height: 3, background: GRAD }} />
            </div>

            <div className="flex items-center gap-2 pb-2.5">
              <span className="h-px flex-1" style={{ background: C.line }} />
              <span className="text-[9.5px] tracking-[0.12em] font-bold" style={{ color: C.muted }}>TRY ASKING</span>
              <span className="h-px flex-1" style={{ background: C.line }} />
            </div>

            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => ask(s)}
                  className="reveal group text-left text-[12px] font-medium bg-white/80 backdrop-blur-sm rounded-2xl px-3.5 py-2.5
                             border transition-all duration-200 ease-smooth
                             hover:-translate-y-0.5 hover:bg-white hover:shadow-soft
                             hover:border-[rgba(56,189,248,0.75)]"
                  style={{ borderColor: C.line, color: C.deep, '--d': `${i * 70}ms` }}>
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-3 h-3 flex-shrink-0 opacity-60 transition-all group-hover:opacity-100 group-hover:rotate-90"
                              style={{ color: C.primaryDk }} strokeWidth={2.2} />
                    {s}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, i) => (
          <div key={i} className={` ${m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}>
            <div
              className={`${m.verbatim ? 'max-w-full w-full' : 'max-w-[86%]'} px-3.5 py-2.5 text-[13px] whitespace-pre-wrap leading-relaxed`}
              style={
                m.role === 'user'
                  ? { background: GRAD, color: '#FFFFFF',
                      borderRadius: '16px 16px 5px 16px',
                      boxShadow: '0 2px 8px -3px rgba(23,37,84,0.22)' }
                  : m.error
                    ? { background: '#FFF1F2', color: '#BE123C',
                        border: '1px solid #FECDD3', borderRadius: '16px 16px 16px 5px' }
                    : { background: 'rgba(255,255,255,0.92)', color: C.ink,
                        border: `1px solid ${C.line}`, borderRadius: '16px 16px 16px 5px',
                        boxShadow: '0 4px 14px -6px rgba(23,37,84,0.18)' }
              }
            >
              {m.verbatim ? (
                <>
                  {m.title && <div className="font-bold mb-1.5 text-[12px]">{m.title}</div>}
                  <pre className="whitespace-pre font-mono text-[10.5px] leading-[1.45] overflow-x-auto
                                  max-h-[340px] overflow-y-auto m-0">{m.content}</pre>
                  <button onClick={() => { navigator.clipboard.writeText(m.content); }}
                          className="mt-2 text-[11px] font-semibold text-[#2563EB] hover:underline">
                    Copy this report
                  </button>
                </>
              ) : m.role === 'assistant' && !m.error ? formatAnswer(m.content) : m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start ">
            <div className="px-4 py-3 flex items-center gap-2"
                 style={{ background: 'rgba(255,255,255,0.92)', border: `1px solid ${C.line}`,
                          borderRadius: '16px 16px 16px 5px',
                          boxShadow: '0 4px 14px -6px rgba(23,37,84,0.18)' }}>
              <span className="inline-flex gap-1.5" aria-label="Thinking">
                {[0, 160, 320].map((d) => (
                  <span key={d} className="w-1.5 h-1.5 rounded-full"
                        style={{ background: GRAD, animation: 'typing-dot 1.1s ease-in-out infinite',
                                 animationDelay: `${d}ms` }} />
                ))}
              </span>
              <span className="text-[11px] font-medium" style={{ color: C.muted }}>Thinking…</span>
            </div>
          </div>
        )}
      </div>

      <div className="px-3 pt-2.5 pb-1.5 flex gap-2 flex-shrink-0 bg-white/80 backdrop-blur-md"
           style={{ borderTop: `1px solid ${C.line}` }}>
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(text); } }}
          maxLength={1000}
          disabled={busy}
          data-testid="ai-assistant-input"
          placeholder="Ask about your HR data…"
          className="flex-1 min-w-0 rounded-2xl px-3.5 py-2.5 text-[13px] focus:outline-none
                     transition-all duration-200 disabled:opacity-60"
          style={{ border: `1px solid ${C.line}`, background: C.tint, color: C.ink }}
          onFocus={(e) => { e.currentTarget.style.borderColor = C.sky;
                            e.currentTarget.style.background = '#FFFFFF';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(56,189,248,0.16)'; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = C.line;
                           e.currentTarget.style.background = 'rgba(56,189,248,0.10)';
                           e.currentTarget.style.boxShadow = 'none'; }}
        />
        <button onClick={() => ask(text)} disabled={busy || !text.trim()}
          data-testid="ai-assistant-send" aria-label="Send"
          className="rounded-2xl px-3.5 text-white flex-shrink-0 transition-all duration-200
                     hover:-translate-y-0.5 active:scale-95
                     disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0"
          style={{ background: GRAD, boxShadow: '0 3px 10px -4px rgba(23,37,84,0.28)' }}>
          <Send className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 pb-2.5 bg-white/80 backdrop-blur-md flex-shrink-0">
        <p className="text-[9.5px] text-center" style={{ color: C.muted }}>
          Read-only &middot; restricted to your access &middot; {COMPANY}
        </p>
      </div>
    </div>
  );
}
